import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const POLL_MS = 120000;
export const LAAFFIC_CHANGED = "laaffic-mapping-changed";
export const notifyLaafficChanged = () => window.dispatchEvent(new Event(LAAFFIC_CHANGED));

export const useLaafficUnmapped = (enabled) => {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => api.get("/admin/integrations/laaffic/unmapped-summary")
      .then((r) => alive && setSummary(r.data)).catch(() => {});
    load();
    const t = setInterval(load, POLL_MS);
    // Mapping saves trigger a background re-check; refetch shortly after.
    const onChange = () => { load(); setTimeout(load, 4000); };
    window.addEventListener(LAAFFIC_CHANGED, onChange);
    return () => { alive = false; clearInterval(t); window.removeEventListener(LAAFFIC_CHANGED, onChange); };
  }, [enabled]);
  return summary;
};

const names = (arr, key) => arr.slice(0, 3).map((x) => x[key]).join(", ") + (arr.length > 3 ? "…" : "");

export const LaafficUnmappedBanner = ({ summary }) => {
  if (!summary || (!summary.total && !summary.blank_tag_mappings)) return null;
  const tagCalls = summary.by_reason?.unmapped_disposition || 0;
  const groupCalls = summary.by_reason?.unmapped_group || 0;
  return (
    <div className="glass-card border-l-4 border-amber-500 bg-amber-50/80 p-3 mb-5 flex items-start gap-3 text-sm" data-testid="laaffic-unmapped-banner">
      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1 space-y-1">
        <div className="font-medium text-amber-900" data-testid="laaffic-unmapped-total">
          {summary.total.toLocaleString()} Laaffic call{summary.total === 1 ? "" : "s"} waiting — not in the dashboard yet
          {summary.reprocessing && <span className="ml-2 text-xs text-amber-700">(re-checking now…)</span>}
        </div>
        {(tagCalls > 0 || summary.blank_tag_mappings > 0) && (
          <div className="text-xs text-amber-800 flex flex-wrap items-center gap-2">
            <span>{tagCalls.toLocaleString()} calls with tags that aren&apos;t mapped{summary.tags?.length ? `: ${names(summary.tags, "label")}` : ""}</span>
            <Link to="/admin/laaffic-dispositions" className="inline-flex items-center gap-1 font-semibold underline" data-testid="laaffic-banner-map-tags">
              Map tags <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
        {groupCalls > 0 && (
          <div className="text-xs text-amber-800 flex flex-wrap items-center gap-2">
            <span>{groupCalls.toLocaleString()} calls from agent groups not linked to a campaign{summary.groups?.length ? `: ${names(summary.groups, "name")}` : ""}</span>
            <Link to="/campaigns" className="inline-flex items-center gap-1 font-semibold underline" data-testid="laaffic-banner-link-groups">
              Set SIP group name <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
        <div className="text-[11px] text-amber-700">Once mapped, waiting calls move into the dashboard automatically.</div>
      </div>
    </div>
  );
};
